/**
 * Article Slug Mapping
 * Maps internal article IDs to localized URL slugs for Romanian and Russian
 * 
 * Structure: Internal slug → { ro: Romanian slug, ru: Russian slug }
 */

export interface ArticleSlugMap {
  ro: string;
  ru: string;
}

/**
 * Article slug mappings
 * Internal English slugs map to localized slugs
 */
export const articleSlugMap: Record<string, ArticleSlugMap> = {
                          'gdpr-moldova-legea-195-2024': {
    ro: 'gdpr-moldova-legea-195-2024',
    ru: 'zakon-195-2024-moldova'
  },
  'cost-externalizare-it-moldova': {
    ro: 'cat-costa-externalizare-it-moldova',
    ru: 'skolko-stoit-it-autsorsing-moldova'
  },
  'cost-site-web-moldova': {
    ro: 'cat-costa-site-web-moldova',
    ru: 'skolko-stoit-sozdanie-sayta-moldova'
  },
  'e-factura-moldova-ghid': {
    ro: 'e-factura-moldova-ghid',
    ru: 'e-faktura-moldova'
  },
  'protectia-datelor-angajatori': {
    ro: 'protectia-datelor-angajatori',
    ru: 'zashchita-dannykh-rabotodateli'
  },
  'suport-tehnic-externalizat': {
    ro: 'suport-tehnic-externalizat',
    ru: 'tekhpodderzhka-autsorsing'
  },
  'erp-pentru-imm': {
    ro: 'erp-pentru-imm',
    ru: 'erp-dlya-biznesa'
  },
  'crm-pentru-afaceri': {
    ro: 'crm-pentru-afaceri',
    ru: 'crm-sistema'
  },
  'integrare-1c': {
    ro: 'integrare-1c',
    ru: 'integratsiya-1c'
  },
  'declaratie-electronica-sfs': {
    ro: 'declaratie-electronica-sfs',
    ru: 'elektronnaya-deklaratsiya'
  },
  'automatizare-ai-firme-moldova': {
    ro: 'automatizare-ai-firme-moldova',
    ru: 'avtomatizatsiya-ii-biznes-moldova'
  },
  'backup-date-companii': {
    ro: 'backup-date-companii',
    ru: 'rezervnoe-kopirovanie-dannykh'
  },
  'securitate-cibernetica-firme': {
    ro: 'securitate-cibernetica-firme',
    ru: 'kiberbezopasnost-biznes'
  },
  'cloud-sau-server-local': {
    ro: 'cloud-sau-server-local',
    ru: 'oblako-ili-svoy-server'
  },
  'lansare-magazin-online': {
    ro: 'lansare-magazin-online',
    ru: 'zapusk-internet-magazina'
  },
  'email-profesional-firma': {
    ro: 'email-profesional-firma',
    ru: 'korporativnaya-pochta-biznes'
  },
  'chatbot-ai-suport-clienti': {
    ro: 'chatbot-ai-suport-clienti',
    ru: 'ii-chat-bot-podderzhka-klientov'
  },
  'migrare-baza-de-date': {
    ro: 'migrare-baza-de-date',
    ru: 'migratsiya-bazy-dannykh'
  },
  'firewall-securitate-retea-firma': {
    ro: 'firewall-securitate-retea-firma',
    ru: 'firewall-zashchita-seti-biznes'
  },
  'monitorizare-it-proactiva': {
    ro: 'monitorizare-it-proactiva',
    ru: 'monitoring-it-infrastruktury'
  },
  'windows-10-fara-suport-firma': {
    ro: 'windows-10-fara-suport-firma',
    ru: 'windows-10-bez-podderzhki-biznes'
  },
  'audit-securitate-it-firma': {
    ro: 'audit-securitate-it-firma',
    ru: 'audit-it-bezopasnosti-biznes'
  },
  'legea-195-2024-baza-de-date-firma': {
    ro: 'legea-195-2024-baza-de-date-firma',
    ru: 'zakon-195-2024-baza-dannykh-firmy'
  },
  'dezvoltare-software-la-comanda': {
    ro: 'dezvoltare-software-la-comanda',
    ru: 'razrabotka-po-na-zakaz'
  },
  'baza-de-date-pentru-firma': {
    ro: 'baza-de-date-pentru-firma',
    ru: 'baza-dannykh-dlya-biznesa'
  },
  'backup-microsoft-365-firma': {
    ro: 'backup-microsoft-365-firma',
    ru: 'backup-microsoft-365-biznes'
  },
  'tip-site-web-firma': {
    ro: 'tip-site-web-firma',
    ru: 'kakoy-sayt-nuzhen-biznesu'
  },
  'plan-continuitate-it-firma': {
    ro: 'plan-continuitate-it-firma',
    ru: 'plan-nepreryvnosti-it-biznesa'
  },
};

/**
 * Reverse mappings for URL parsing
 */
const createReverseMap = (): Record<string, string> => {
  const reverseMap: Record<string, string> = {};
  
  Object.entries(articleSlugMap).forEach(([internalSlug, localizedSlugs]) => {
    reverseMap[localizedSlugs.ro] = internalSlug;
    reverseMap[localizedSlugs.ru] = internalSlug;
    // Also keep the original English slug for backward compatibility during migration
    reverseMap[internalSlug] = internalSlug;
  });
  
  return reverseMap;
};

export const reverseArticleSlugMap = createReverseMap();

/**
 * Get localized article slug
 */
export function getLocalizedArticleSlug(internalSlug: string, language: 'ro' | 'ru'): string {
  return articleSlugMap[internalSlug]?.[language] || internalSlug;
}

/**
 * Get internal article slug from localized slug
 */
export function getInternalArticleSlug(localizedSlug: string): string {
  return reverseArticleSlugMap[localizedSlug] || localizedSlug;
}

/**
 * Get all internal article slugs
 */
export function getAllInternalArticleSlugs(): string[] {
  return Object.keys(articleSlugMap);
}

/**
 * Get all localized article slugs for a language
 */
export function getAllLocalizedArticleSlugs(language: 'ro' | 'ru'): string[] {
  return Object.values(articleSlugMap).map(slugs => slugs[language]);
}


